import { useMemo, useState } from 'react';
import {
  updateFinanceContactRequest,
  duplicateFlagMatchReasonLabel,
  type DuplicateContactFlagDto,
  type DuplicateFlagContactDto,
  type UpdateFinanceContactRequest,
  ErrorCode,
  hasErrorCode,
  isStaleWrite,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  useMergeContacts,
  useDismissDuplicateFlag,
  useUpdateFinanceContact,
} from '@/hooks/queries';
import { formatAmount } from '@/components/finance/utils';
import { cn } from '@/lib/utils';
import { CLIENT_CONFLICT_MARKER } from '@/lib/clientConflict';

// Duplicate-donor resolution panel — the inline ledger surface a treasurer uses
// to collapse two split records for the same donor into one BEFORE filing (a
// duplicate understates the donor's aggregate + limit math). Merge keeps the
// survivor and folds the loser's history in; Dismiss marks the pair genuinely
// different. Both leave the flag resolved and refresh the ledger.
//
// KEY design decision (server contract): the merge is SURVIVOR-WINS-ON-CONFLICT
// — it keeps the survivor's non-blank fields and fills only the survivor's BLANK
// fields from the loser. There is NO per-field override in the merge request. So
// the only fields worth choosing are TRUE CONFLICTS (both sides non-blank AND
// differing); a treasurer who flips one to "take other" is applied by
// PRE-EDITING the survivor (a PUT) so the survivor then wins with the corrected
// value. The common path (no flips) is a single merge call, no PUT.

// ── Pure helpers (unit-tested) ──────────────────────────────────────────────

/**
 * Which side should be the default survivor, in precedence order:
 *  (a) exactly one side `isDesignated` → that side (a designated contact — the
 *      candidate, a fee payee — is the forced survivor);
 *  (b) else exactly one side `isOnFiledReport` → that side (never re-key a filed
 *      record's id);
 *  (c) else the side with the earlier `earliestContributionDate` — the "older
 *      record" proxy (FinanceContact has no CreatedAt). A null date sorts LAST:
 *      a dated record beats an undated one;
 *  (d) tie / both null → `contactA`.
 */
export function pickDefaultSurvivorId(flag: DuplicateContactFlagDto): string {
  const { contactA, contactB } = flag;

  // (a) exactly one designated.
  if (contactA.isDesignated !== contactB.isDesignated) {
    return contactA.isDesignated ? contactA.contactId : contactB.contactId;
  }

  // (b) exactly one on a filed report.
  if (contactA.isOnFiledReport !== contactB.isOnFiledReport) {
    return contactA.isOnFiledReport ? contactA.contactId : contactB.contactId;
  }

  // (c) earlier earliestContributionDate wins; a null date sorts last.
  const dateA = contactA.earliestContributionDate;
  const dateB = contactB.earliestContributionDate;
  if (dateA !== dateB) {
    if (dateA == null) return contactB.contactId; // A undated, B dated → B.
    if (dateB == null) return contactA.contactId; // B undated, A dated → A.
    // Both dated (ISO yyyy-MM-dd compares lexicographically) — earlier wins.
    return dateA < dateB ? contactA.contactId : contactB.contactId;
  }

  // (d) tie / both null.
  return contactA.contactId;
}

/** A field where both records disagree — the survivor keeps its value unless flipped. */
export interface FieldConflict {
  /** The `UpdateFinanceContactRequest` field key (also the choice-state key). */
  key: string;
  /** Treasurer label. */
  label: string;
  survivorValue: string;
  otherValue: string;
}

// The editable identity/address fields the merge can carry, with treasurer
// labels. Order matches the render order. Name parts lead (they re-validate as a
// group), then contact + address + occupation/employer.
const CONFLICT_FIELDS: { key: keyof DuplicateFlagContactDto; label: string }[] = [
  { key: 'firstName', label: 'First name' },
  { key: 'middleName', label: 'Middle' },
  { key: 'lastName', label: 'Last name' },
  { key: 'nameSuffix', label: 'Suffix' },
  { key: 'email', label: 'Email' },
  { key: 'address', label: 'Address' },
  { key: 'addressLine2', label: 'Address line 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipCode', label: 'ZIP' },
  { key: 'occupation', label: 'Occupation' },
  { key: 'employer', label: 'Employer' },
  { key: 'employmentLocation', label: 'Employment location' },
];

/** The four name-part keys re-validated as a group when any one changes. */
const NAME_PART_KEYS = ['firstName', 'middleName', 'lastName', 'nameSuffix'] as const;

function fieldStr(contact: DuplicateFlagContactDto, key: keyof DuplicateFlagContactDto): string {
  const v = contact[key];
  return typeof v === 'string' ? v : '';
}

/**
 * The fields that TRULY conflict: both sides non-blank (after trim) AND differing
 * (trimmed compare). Blank-vs-value and identical values are excluded — the merge
 * auto-combines blanks, and identical values need no choice.
 */
export function conflictingFields(
  survivor: DuplicateFlagContactDto,
  other: DuplicateFlagContactDto,
): FieldConflict[] {
  const conflicts: FieldConflict[] = [];
  for (const { key, label } of CONFLICT_FIELDS) {
    const s = fieldStr(survivor, key).trim();
    const o = fieldStr(other, key).trim();
    if (s === '' || o === '') continue; // a blank side is auto-filled, not a conflict.
    if (s === o) continue; // identical — no choice needed.
    conflicts.push({
      key,
      label,
      survivorValue: fieldStr(survivor, key),
      otherValue: fieldStr(other, key),
    });
  }
  return conflicts;
}

// ── Component ────────────────────────────────────────────────────────────────

type FieldChoice = 'keep' | 'take';

/**
 * `Merged` → brief success line, then collapse.
 * `amend` → a filed report covers a touched date; confirm to amend (re-merge with
 *   the SAME survivor version, `confirmAmendment: true`, no re-PUT).
 * `blocked` → both designated (or another hard reason) — resolve manually.
 */
type MergeStage =
  | { kind: 'form' }
  | { kind: 'amend'; reportNames: string[]; survivorVersion: number }
  | { kind: 'blocked'; reason: string }
  | { kind: 'merged' };

export function DuplicateResolvePanel({
  flag,
  rowContactId,
  isPayee = false,
  onDone,
}: {
  flag: DuplicateContactFlagDto;
  /** The contact id of the ledger row this panel is nested under (context only). */
  rowContactId: string;
  /**
   * True when this pair is a vendor/PAYEE duplicate (an Expenditure row) rather than
   * a donor duplicate. Switches the treasurer-facing copy from "donor" to "payee"
   * and hides the donor-only per-election (P/G) totals, which are always $0 for a
   * payee. The merge mechanics are identical — ContactMergeService repoints
   * expenditures/loans/debts the same way it repoints contributions.
   */
  isPayee?: boolean;
  onDone: () => void;
}) {
  const party = isPayee ? 'payee' : 'donor';
  const [survivorId, setSurvivorId] = useState<string>(() =>
    pickDefaultSurvivorId(flag),
  );
  const [stage, setStage] = useState<MergeStage>({ kind: 'form' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { mutateAsync: mergeContacts } = useMergeContacts();
  const { mutateAsync: dismissFlag } = useDismissDuplicateFlag();
  const { mutateAsync: updateContact } = useUpdateFinanceContact();

  // A designated contact is the forced survivor — lock the chooser to it. If both
  // are designated the chooser stays locked and the merge returns Blocked.
  const eitherDesignated = flag.contactA.isDesignated || flag.contactB.isDesignated;

  const survivor = survivorId === flag.contactA.contactId ? flag.contactA : flag.contactB;
  const loser = survivorId === flag.contactA.contactId ? flag.contactB : flag.contactA;

  const conflicts = useMemo(
    () => conflictingFields(survivor, loser),
    [survivor, loser],
  );

  // Per-field choice, keyed by field key. Defaults to "keep" for every conflict;
  // resets whenever the survivor flips (the survivor's values are the new
  // baseline, so the prior "take" picks no longer mean the same thing).
  const [choices, setChoices] = useState<Record<string, FieldChoice>>({});
  const choiceFor = (key: string): FieldChoice => choices[key] ?? 'keep';

  const handleSurvivorChange = (nextId: string | undefined) => {
    // Radix single-select hands back `''`/undefined when the active chip is
    // toggled off — ignore that so a survivor is always chosen.
    if (!nextId || nextId === survivorId) return;
    if (eitherDesignated) return; // locked.
    setSurvivorId(nextId);
    setChoices({}); // reset picks to "keep" against the new baseline.
    setError(null);
    setStage({ kind: 'form' });
  };

  // Run the merge, discriminating on the outcome. `survivorVersion` is the
  // survivor's version AFTER any pre-edit PUT; the amend re-call reuses it
  // verbatim (no second PUT). Shared by the first pass and the amend confirm.
  const runMerge = async (survivorVersion: number, confirmAmendment: boolean) => {
    const result = await mergeContacts({
      survivorContactId: survivorId,
      loserContactId: loser.contactId,
      survivorConcurrencyVersion: survivorVersion,
      loserConcurrencyVersion: loser.concurrencyVersion,
      confirmAmendment,
    });
    switch (result.outcome) {
      case 'Merged':
        setStage({ kind: 'merged' });
        // Show the success line briefly, then collapse (the ledger refresh from
        // the hook's invalidation drops the badge). No toast library here — the
        // in-panel confirmation + the row refresh IS the success feedback.
        window.setTimeout(onDone, 700);
        break;
      case 'NeedsAmendmentConfirmation':
        setStage({
          kind: 'amend',
          reportNames: result.affectedFiledReportNames,
          survivorVersion,
        });
        break;
      case 'Blocked':
        setStage({
          kind: 'blocked',
          reason:
            result.blockReason ??
            'This pair can’t be merged automatically — resolve it manually.',
        });
        break;
    }
  };

  const handleMerge = async () => {
    setError(null);
    setBusy(true);
    try {
      // Which conflicts the treasurer flipped to "take other" → the corrected
      // final values for the survivor.
      const flipped = conflicts.filter((c) => choiceFor(c.key) === 'take');
      const overrides: Record<string, string> = {};
      for (const c of flipped) overrides[c.key] = c.otherValue;

      // If ANY name part is flipped, send ALL FOUR desired final name-part values
      // — the backend re-validates the whole name shape when any part changes, so
      // an unflipped part must still carry the survivor's current value.
      const nameFlipped = flipped.some((c) =>
        (NAME_PART_KEYS as readonly string[]).includes(c.key),
      );
      if (nameFlipped) {
        for (const key of NAME_PART_KEYS) {
          overrides[key] = choiceFor(key) === 'take'
            ? fieldStr(loser, key)
            : fieldStr(survivor, key);
        }
      }

      let survivorVersion = survivor.concurrencyVersion;

      // Pre-edit the survivor so it WINS with the corrected value, then merge with
      // the bumped version. No overrides → no PUT (the common path).
      if (Object.keys(overrides).length > 0) {
        const data: UpdateFinanceContactRequest = updateFinanceContactRequest({
          ...overrides,
          concurrencyVersion: survivor.concurrencyVersion,
        });
        const updated = await updateContact({ id: survivorId, data });
        survivorVersion = updated.concurrencyVersion;
      }

      await runMerge(survivorVersion, false);
    } catch (err) {
      setError(mergeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmAmend = async () => {
    if (stage.kind !== 'amend') return;
    setError(null);
    setBusy(true);
    try {
      await runMerge(stage.survivorVersion, true);
    } catch (err) {
      setError(mergeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    setError(null);
    setBusy(true);
    try {
      await dismissFlag(flag.flagId);
      setStage({ kind: 'merged' }); // reuse the "resolved" confirmation shape.
      window.setTimeout(onDone, 700);
    } catch (err) {
      setError(mergeErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const merged = stage.kind === 'merged';

  return (
    <div
      data-testid={`duplicate-resolve-panel-${rowContactId}`}
      className="mb-4 rounded-md border border-amber-300 bg-amber-50/60 p-4"
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-amber-900">
          Possible duplicate {party}
        </span>
        <Badge variant="secondary">
          {duplicateFlagMatchReasonLabel[flag.matchReason]}
        </Badge>
        {flag.provenance === 'Backfill' && (
          <span className="text-xs text-muted-foreground">from sweep</span>
        )}
      </div>

      {merged ? (
        <p
          role="status"
          data-testid={`duplicate-resolve-done-${rowContactId}`}
          className="text-sm font-medium text-green-700"
        >
          Resolved.
        </p>
      ) : (
        <>
          <p className="mb-3 text-sm text-muted-foreground">
            These two records look like the same {party}. Keep one (the survivor);
            its history and totals absorb the other.
          </p>

          {/* Survivor chooser — a single-select A/B toggle, locked to a
              designated record when either side is designated. */}
          <div className="mb-3">
            <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Keep this record
            </div>
            <ToggleGroup
              type="single"
              variant="outline"
              value={survivorId}
              onValueChange={handleSurvivorChange}
              disabled={eitherDesignated}
              data-testid={`duplicate-resolve-survivor-${rowContactId}`}
              aria-label="Choose which record to keep"
              className="flex-wrap items-stretch"
            >
              <ToggleGroupItem
                value={flag.contactA.contactId}
                data-testid={`duplicate-resolve-survivor-a-${rowContactId}`}
                className="h-auto flex-col items-start whitespace-normal py-2 text-left"
              >
                <ContactSummaryLabel contact={flag.contactA} isPayee={isPayee} />
              </ToggleGroupItem>
              <ToggleGroupItem
                value={flag.contactB.contactId}
                data-testid={`duplicate-resolve-survivor-b-${rowContactId}`}
                className="h-auto flex-col items-start whitespace-normal py-2 text-left"
              >
                <ContactSummaryLabel contact={flag.contactB} isPayee={isPayee} />
              </ToggleGroupItem>
            </ToggleGroup>
            {eitherDesignated && (
              <p className="mt-1 text-xs text-muted-foreground">
                Locked to the designated record.
              </p>
            )}
          </div>

          <Separator className="my-3" />

          {/* Conflicting fields — the ONLY fields the treasurer chooses (blanks
              auto-combine). Each defaults to "keep survivor". */}
          {conflicts.length === 0 ? (
            <p
              data-testid={`duplicate-resolve-no-conflicts-${rowContactId}`}
              className="text-sm text-muted-foreground"
            >
              No conflicting fields — details will be combined.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                These fields differ — pick which to keep
              </div>
              {conflicts.map((c) => (
                <div
                  key={c.key}
                  data-testid={`duplicate-resolve-conflict-${c.key}-${rowContactId}`}
                  className="flex flex-col gap-1.5"
                >
                  <div className="text-sm font-medium">{c.label}</div>
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    value={choiceFor(c.key)}
                    onValueChange={(v) => {
                      if (!v) return; // ignore toggle-off; a choice is always set.
                      setChoices((prev) => ({ ...prev, [c.key]: v as FieldChoice }));
                    }}
                    aria-label={`Choose ${c.label}`}
                    data-testid={`duplicate-resolve-conflict-toggle-${c.key}-${rowContactId}`}
                    className="flex-wrap items-stretch"
                  >
                    <ToggleGroupItem
                      value="keep"
                      className="h-auto flex-col items-start whitespace-normal py-1.5 text-left"
                    >
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Survivor
                      </span>
                      <span className="text-sm">{c.survivorValue || '—'}</span>
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="take"
                      className="h-auto flex-col items-start whitespace-normal py-1.5 text-left"
                    >
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        Other
                      </span>
                      <span className="text-sm">{c.otherValue || '—'}</span>
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              ))}
            </div>
          )}

          {/* NeedsAmendmentConfirmation heads-up — a filed report covers a touched
              date. Confirm to amend (re-merge with confirmAmendment). */}
          {stage.kind === 'amend' && (
            <div
              role="alert"
              data-testid={`duplicate-resolve-amend-${rowContactId}`}
              className="mt-3 rounded-md border border-amber-400 bg-amber-100/70 p-3"
            >
              <p className="text-sm text-amber-900">
                These records are on filed report
                {stage.reportNames.length === 1 ? '' : 's'}:{' '}
                <span className="font-medium">
                  {stage.reportNames.join(', ')}
                </span>
                . Merging will require amending them.
              </p>
              <Button
                type="button"
                size="sm"
                className="mt-2"
                disabled={busy}
                onClick={handleConfirmAmend}
                data-testid={`duplicate-resolve-confirm-amend-${rowContactId}`}
              >
                Confirm &amp; merge
              </Button>
            </div>
          )}

          {/* Blocked — both designated (or another hard reason). No merge path. */}
          {stage.kind === 'blocked' && (
            <p
              role="alert"
              data-testid={`duplicate-resolve-blocked-${rowContactId}`}
              className="mt-3 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700"
            >
              {stage.reason}
            </p>
          )}

          {error && (
            <p
              role="alert"
              data-testid={`duplicate-resolve-error-${rowContactId}`}
              className="mt-3 text-sm text-rose-700"
            >
              {error}
            </p>
          )}

          {/* Primary + secondary actions. The primary Merge is hidden once an
              amend heads-up is up (the treasurer confirms via that button). */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {stage.kind !== 'amend' && (
              <Button
                type="button"
                disabled={busy}
                onClick={handleMerge}
                data-testid={`duplicate-resolve-merge-${rowContactId}`}
              >
                Merge
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={handleDismiss}
              data-testid={`duplicate-resolve-dismiss-${rowContactId}`}
            >
              Not a duplicate
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** One record, compactly: display name, email, address one-liner, totals + badges. */
function ContactSummaryLabel({
  contact,
  isPayee,
}: {
  contact: DuplicateFlagContactDto;
  isPayee: boolean;
}) {
  const addressLine = [
    contact.address,
    contact.addressLine2,
    contact.city,
    contact.state,
    contact.zipCode,
  ]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(', ');

  return (
    <span className="flex flex-col gap-0.5">
      <span className="text-sm font-medium">{contact.displayName}</span>
      {contact.email && (
        <span className="text-xs text-muted-foreground">{contact.email}</span>
      )}
      {addressLine && (
        <span className="text-xs text-muted-foreground">{addressLine}</span>
      )}
      {/* Per-election (P/G) totals are donor-only — a payee has no contributions,
          so the figures are always $0 and the line is noise. Hidden for payees. */}
      {!isPayee && (
        <span className="text-xs text-muted-foreground">
          {formatAmount(contact.calendarYearTotal)} this year · P{' '}
          {formatAmount(contact.primaryTotal)} · G {formatAmount(contact.generalTotal)}
        </span>
      )}
      <span className="mt-0.5 flex flex-wrap gap-1">
        {contact.isDesignated && contact.designationLabel && (
          <Badge variant="secondary" className={cn('text-[10px]')}>
            {contact.designationLabel}
          </Badge>
        )}
        {contact.isOnFiledReport && (
          <span className="text-[10px] text-muted-foreground">
            on a filed report
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * Merge/dismiss error → treasurer copy. A concurrency conflict (the panel went
 * stale) gets the refresh prompt; anything else surfaces its message.
 */
export function mergeErrorMessage(err: unknown): string {
  // The merge hook throws a bare CLIENT_CONFLICT_MARKER on a ConcurrencyConflict 409; the survivor pre-edit carries the code.
  if (
    (err instanceof Error && err.message === CLIENT_CONFLICT_MARKER) ||
    isStaleWrite(err)
  ) {
    return 'This donor changed — refresh and try again.';
  }
  return err instanceof Error ? err.message : 'Something went wrong.';
}
